import React, { useMemo, useState } from "react";
import dayjs from "dayjs";
import {
  Alert,
  Button,
  Collapse,
  DatePicker,
  Form,
  Input,
  Select,
  Skeleton,
  Spin,
  Switch,
  Tag,
  Tooltip,
} from "antd";
import { FileExcelOutlined } from "@ant-design/icons";
import { CSVLink } from "react-csv";
import { useLazyFetchStockSummaryDatewiseReportQuery } from "features/store";

const { Panel } = Collapse;
const MATERIAL_TYPES = ["CLOTH MATERIAL", "CAP MATERIAL", "OTHER MATERIAL"];

// Format "2026-04-01" → "Apr 01"
const fmtHeader = (dateStr) => {
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-IN", { month: "short", day: "2-digit" });
};

// Format "2026-04-01" → "Apr 01, 2026"
const fmtLong = (dateStr) => {
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-IN", { month: "short", day: "2-digit", year: "numeric" });
};

const ColorSwatch = ({ color, code }) => (
  <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
    {code && (
      <span
        style={{
          display: "inline-block",
          width: 14,
          height: 14,
          borderRadius: 3,
          background: `#${code}`,
          border: "1px solid #d9d9d9",
          flexShrink: 0,
        }}
      />
    )}
    {color}
  </span>
);

// Sticky column cell style helpers
const stickyLeft = (left, bg = "#fafafa", zIndex = 2) => ({
  position: "sticky",
  left,
  background: bg,
  zIndex,
  boxShadow: "2px 0 4px -2px rgba(0,0,0,0.08)",
});

const COL_WIDTHS = { num: 48, type: 140, name: 160, color: 140, unit: 80 };
const LEFT_OFFSETS = {
  num: 0,
  type: COL_WIDTHS.num,
  name: COL_WIDTHS.num + COL_WIDTHS.type,
  color: COL_WIDTHS.num + COL_WIDTHS.type + COL_WIDTHS.name,
  unit: COL_WIDTHS.num + COL_WIDTHS.type + COL_WIDTHS.name + COL_WIDTHS.color,
};

const thBase = {
  padding: "8px 10px",
  fontWeight: 600,
  fontSize: 13,
  borderBottom: "2px solid #e8e8e8",
  whiteSpace: "nowrap",
  background: "#fafafa",
};

const tdBase = {
  padding: "7px 10px",
  fontSize: 13,
  borderBottom: "1px solid #f0f0f0",
  whiteSpace: "nowrap",
};

const InvRepStockSummaryDatewise = () => {
  const [form] = Form.useForm();
  const [fieldErrors, setFieldErrors] = useState({});
  const [clientError, setClientError] = useState("");
  const [hasFetched, setHasFetched] = useState(false);
  const [trigger, { data, isLoading, isFetching, error }] =
    useLazyFetchStockSummaryDatewiseReportQuery();

  // Default: current month start → today
  const defaultFrom = dayjs().startOf("month");
  const defaultTo = dayjs();

  const validate = (v) => {
    if (!v.date_from) return "Date From is required.";
    if (!v.date_to) return "Date To is required.";
    if (v.date_from.isAfter(v.date_to)) return "Date From must be before Date To.";
    if (v.date_to.diff(v.date_from, "day") > 90) return "Date range cannot exceed 90 days.";
    return null;
  };

  const handleApply = async () => {
    setFieldErrors({});
    setClientError("");
    const v = form.getFieldsValue();
    const err = validate(v);
    if (err) { setClientError(err); return; }

    const params = {
      date_from: v.date_from.format("YYYY-MM-DD"),
      date_to: v.date_to.format("YYYY-MM-DD"),
    };
    if (v.type) params.type = v.type;
    if (v.color) params.color = v.color;
    if (v.cloth_type) params.cloth_type = v.cloth_type;
    if (v.cap_material) params.cap_material = v.cap_material;
    if (v.unit) params.unit = v.unit;
    params.show_zero_rows = v.show_zero_rows !== false ? "true" : "false";

    setHasFetched(true);
    const result = await trigger(params);
    if (result?.error?.status === 400 && result.error.data?.errors) {
      setFieldErrors(result.error.data.errors);
    }
  };

  const handleClear = () => {
    form.resetFields();
    setFieldErrors({});
    setClientError("");
    setHasFetched(false);
  };

  const dates = data?.dates || [];
  const results = data?.results || [];
  const columnTotals = data?.column_totals || {};
  const count = data?.count ?? results.length;
  const grandTotal = data?.grand_total_added;

  // Build CSV data from pivot table
  const csvData = useMemo(() => {
    if (!results.length) return [];
    return results.map((row) => {
      const obj = {
        material_type: row.material_type,
        material_name: row.material_name,
        color: row.color,
        unit: row.unit,
        total_added: row.total_added,
        current_quantity: row.current_quantity,
      };
      dates.forEach((d) => { obj[d] = row.by_date?.[d] ?? 0; });
      return obj;
    });
  }, [results, dates]);

  const csvHeaders = useMemo(() => {
    const fixed = [
      { label: "Material Type", key: "material_type" },
      { label: "Material Name", key: "material_name" },
      { label: "Color", key: "color" },
      { label: "Unit", key: "unit" },
    ];
    const dateCols = dates.map((d) => ({ label: d, key: d }));
    return [
      ...fixed,
      ...dateCols,
      { label: "Total Added", key: "total_added" },
      { label: "Current Stock", key: "current_quantity" },
    ];
  }, [dates]);

  const apiError = error?.status === 400 && !error?.data?.errors
    ? (error?.data?.detail || "Invalid filter values.")
    : null;

  return (
    <div style={{ padding: "24px" }}>
      <h2 style={{ marginBottom: 16 }}>Stock Summary (Datewise) Report</h2>

      {/* ── Filters ── */}
      <Collapse defaultActiveKey={["filters"]} style={{ marginBottom: 16 }}>
        <Panel header="Filters" key="filters">
          <Form
            form={form}
            layout="inline"
            initialValues={{ date_from: defaultFrom, date_to: defaultTo, show_zero_rows: true }}
            style={{ flexWrap: "wrap", gap: "8px 0" }}
          >
            <Form.Item
              name="date_from"
              label="Date From"
              required
              validateStatus={fieldErrors.date_from ? "error" : ""}
              help={fieldErrors.date_from}
            >
              <DatePicker />
            </Form.Item>
            <Form.Item
              name="date_to"
              label="Date To"
              required
              validateStatus={fieldErrors.date_to ? "error" : ""}
              help={fieldErrors.date_to}
            >
              <DatePicker />
            </Form.Item>
            <Form.Item name="type" label="Material Type">
              <Select allowClear placeholder="All types" style={{ width: 180 }}>
                {MATERIAL_TYPES.map((t) => (
                  <Select.Option key={t} value={t}>{t}</Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="cloth_type" label="Cloth Type">
              <Input placeholder="e.g. TWILL" style={{ width: 130 }} />
            </Form.Item>
            <Form.Item name="cap_material" label="Cap Material">
              <Input placeholder="Cap material" style={{ width: 130 }} />
            </Form.Item>
            <Form.Item name="color" label="Color">
              <Input placeholder="Color" style={{ width: 120 }} />
            </Form.Item>
            <Form.Item name="unit" label="Unit">
              <Select allowClear placeholder="All" style={{ width: 110 }}>
                <Select.Option value="Meter">Meter</Select.Option>
                <Select.Option value="Pieces">Pieces</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item name="show_zero_rows" label="Show Zero Rows" valuePropName="checked">
              <Switch defaultChecked />
            </Form.Item>
          </Form>

          {clientError && (
            <Alert type="error" message={clientError} style={{ marginTop: 8 }} showIcon />
          )}
          {apiError && (
            <Alert type="error" message={apiError} style={{ marginTop: 8 }} showIcon />
          )}

          <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
            <Button type="primary" onClick={handleApply} loading={isLoading || isFetching}>
              Apply Filters
            </Button>
            <Button onClick={handleClear}>Clear Filters</Button>
          </div>
        </Panel>
      </Collapse>

      {/* ── Prompt before first fetch ── */}
      {!hasFetched && (
        <div
          style={{
            textAlign: "center",
            padding: "48px 24px",
            color: "#8c8c8c",
            background: "#fafafa",
            border: "1px dashed #d9d9d9",
            borderRadius: 8,
          }}
        >
          Select a date range and click <b>Apply Filters</b> to load the report.
        </div>
      )}

      {/* ── Loading skeleton ── */}
      {hasFetched && (isLoading || isFetching) && (
        <Spin spinning>
          <Skeleton active paragraph={{ rows: 8 }} />
        </Spin>
      )}

      {/* ── Results ── */}
      {hasFetched && !isLoading && !isFetching && data && (
        <>
          {/* Summary cards */}
          <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
            <Tag color="blue" style={{ fontSize: 14, padding: "4px 10px" }}>
              Items: {count}
            </Tag>
            <Tag color="green" style={{ fontSize: 14, padding: "4px 10px" }}>
              Total Procured: {grandTotal != null ? Number(grandTotal).toLocaleString("en-IN") : "—"}
            </Tag>
            {dates.length > 0 && (
              <Tag color="purple" style={{ fontSize: 14, padding: "4px 10px" }}>
                Range: {fmtLong(dates[0])} – {fmtLong(dates[dates.length - 1])}
              </Tag>
            )}
            {results.length > 0 && (
              <Tooltip title="Export to Excel">
                <CSVLink data={csvData} headers={csvHeaders} filename="stock-summary-datewise.csv">
                  <Button icon={<FileExcelOutlined />} style={{ color: "#217346", borderColor: "#217346" }}>
                    Export to Excel
                  </Button>
                </CSVLink>
              </Tooltip>
            )}
          </div>

          {/* No results */}
          {results.length === 0 && (
            <div
              style={{
                textAlign: "center",
                padding: "48px 24px",
                color: "#8c8c8c",
                background: "#fafafa",
                border: "1px dashed #d9d9d9",
                borderRadius: 8,
              }}
            >
              No data found for the selected filters.
            </div>
          )}

          {/* Pivot table */}
          {results.length > 0 && (
            <div style={{ overflowX: "auto", borderRadius: 8, border: "1px solid #e8e8e8" }}>
              <table style={{ borderCollapse: "collapse", minWidth: "100%", tableLayout: "fixed" }}>
                {/* ── Column width hints ── */}
                <colgroup>
                  <col style={{ width: COL_WIDTHS.num }} />
                  <col style={{ width: COL_WIDTHS.type }} />
                  <col style={{ width: COL_WIDTHS.name }} />
                  <col style={{ width: COL_WIDTHS.color }} />
                  <col style={{ width: COL_WIDTHS.unit }} />
                  {dates.map((d) => <col key={d} style={{ width: 90 }} />)}
                  <col style={{ width: 110 }} />
                  <col style={{ width: 110 }} />
                </colgroup>

                {/* ── Header ── */}
                <thead>
                  <tr>
                    <th style={{ ...thBase, ...stickyLeft(LEFT_OFFSETS.num, "#fafafa", 3), width: COL_WIDTHS.num, textAlign: "center" }}>#</th>
                    <th style={{ ...thBase, ...stickyLeft(LEFT_OFFSETS.type, "#fafafa", 3), width: COL_WIDTHS.type }}>Material Type</th>
                    <th style={{ ...thBase, ...stickyLeft(LEFT_OFFSETS.name, "#fafafa", 3), width: COL_WIDTHS.name }}>Material Name</th>
                    <th style={{ ...thBase, ...stickyLeft(LEFT_OFFSETS.color, "#fafafa", 3), width: COL_WIDTHS.color }}>Color</th>
                    <th style={{ ...thBase, ...stickyLeft(LEFT_OFFSETS.unit, "#fafafa", 3), width: COL_WIDTHS.unit }}>Unit</th>
                    {dates.map((d) => (
                      <th key={d} style={{ ...thBase, textAlign: "right", width: 90 }}>{fmtHeader(d)}</th>
                    ))}
                    <th style={{ ...thBase, textAlign: "right", width: 110 }}>Total Added</th>
                    <th style={{ ...thBase, textAlign: "right", width: 110 }}>Current Stock</th>
                  </tr>
                </thead>

                {/* ── Body ── */}
                <tbody>
                  {results.map((row, idx) => (
                    <tr key={row.inventory_id ?? idx} style={{ background: idx % 2 === 0 ? "#fff" : "#fafafa" }}>
                      <td style={{ ...tdBase, ...stickyLeft(LEFT_OFFSETS.num, idx % 2 === 0 ? "#fff" : "#fafafa"), textAlign: "center", color: "#8c8c8c" }}>{idx + 1}</td>
                      <td style={{ ...tdBase, ...stickyLeft(LEFT_OFFSETS.type, idx % 2 === 0 ? "#fff" : "#fafafa") }}>{row.material_type}</td>
                      <td style={{ ...tdBase, ...stickyLeft(LEFT_OFFSETS.name, idx % 2 === 0 ? "#fff" : "#fafafa") }}>{row.material_name}</td>
                      <td style={{ ...tdBase, ...stickyLeft(LEFT_OFFSETS.color, idx % 2 === 0 ? "#fff" : "#fafafa") }}>
                        <ColorSwatch color={row.color} code={row.color_code} />
                      </td>
                      <td style={{ ...tdBase, ...stickyLeft(LEFT_OFFSETS.unit, idx % 2 === 0 ? "#fff" : "#fafafa"), color: "#595959" }}>{row.unit}</td>
                      {dates.map((d) => {
                        const val = row.by_date?.[d] ?? 0;
                        const hasQty = val > 0;
                        return (
                          <td
                            key={d}
                            style={{
                              ...tdBase,
                              textAlign: "right",
                              background: hasQty ? "#e6f7ff" : undefined,
                              color: hasQty ? "#096dd9" : "#bfbfbf",
                              fontWeight: hasQty ? 600 : 400,
                            }}
                          >
                            {hasQty ? Number(val).toLocaleString("en-IN") : "—"}
                          </td>
                        );
                      })}
                      <td style={{ ...tdBase, textAlign: "right", fontWeight: row.total_added > 0 ? 700 : 400 }}>
                        {row.total_added > 0 ? Number(row.total_added).toLocaleString("en-IN") : "—"}
                      </td>
                      <td style={{ ...tdBase, textAlign: "right" }}>
                        {row.current_quantity != null ? Number(row.current_quantity).toFixed(2) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>

                {/* ── Footer: column totals ── */}
                <tfoot>
                  <tr style={{ background: "#f0f5ff" }}>
                    <td colSpan={5} style={{ ...tdBase, ...stickyLeft(LEFT_OFFSETS.num, "#f0f5ff"), fontWeight: 700, color: "#1d39c4" }}>
                      Column Totals
                    </td>
                    {dates.map((d) => {
                      const val = columnTotals[d] ?? 0;
                      return (
                        <td
                          key={d}
                          style={{
                            ...tdBase,
                            textAlign: "right",
                            fontWeight: 700,
                            color: val > 0 ? "#1d39c4" : "#bfbfbf",
                          }}
                        >
                          {val > 0 ? Number(val).toLocaleString("en-IN") : "—"}
                        </td>
                      );
                    })}
                    <td style={{ ...tdBase, textAlign: "right", fontWeight: 700, color: "#1d39c4" }}>
                      {grandTotal != null ? Number(grandTotal).toLocaleString("en-IN") : "—"}
                    </td>
                    <td style={{ ...tdBase }} />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default InvRepStockSummaryDatewise;
